import React, { useState } from 'react';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import DynamicFormOutlinedIcon from '@mui/icons-material/DynamicFormOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import { deriveDisplayName, deriveOrgInitial, derivePreviewOrgName } from './signupPreviewUtils';
import {
  SIGNUP_PREVIEW_PRIMARY_ITEMS,
  SIGNUP_PREVIEW_RESOURCE_ITEMS,
} from './signupPreviewMenu';
import SignupPreviewPageDemo from './SignupPreviewPageDemos';
import { stringAvatar } from '../../../utils/AvatarUtils';
import './signupLivePreview.css';
import './signupPreviewDemos.css';

const PREVIEW_ICONS = {
  Home: HomeOutlinedIcon,
  Chats: ChatBubbleOutlineIcon,
  Boards: ViewKanbanOutlinedIcon,
  Inbox: InboxOutlinedIcon,
  Tables: TableChartOutlinedIcon,
  Agents: SmartToyOutlinedIcon,
  Teams: WorkspacesOutlinedIcon,
  Workflows: AccountTreeOutlinedIcon,
  Files: FolderOutlinedIcon,
  Artifacts: DynamicFormOutlinedIcon,
  Docs: MenuBookOutlinedIcon,
  Secrets: VpnKeyOutlinedIcon,
  'Custom Functions': CodeOutlinedIcon,
  Forms: DescriptionOutlinedIcon,
  'MCP Servers': HubOutlinedIcon,
  Humans: GroupsOutlinedIcon,
  Trash: DeleteOutlineOutlinedIcon,
  More: MoreHorizOutlinedIcon,
};

function PreviewNavItem({ label, displayLabel, selected = false, onSelect }) {
  const Icon = PREVIEW_ICONS[label] || HomeOutlinedIcon;

  return (
    <button
      type="button"
      className={[
        'signup-preview__nav-item',
        selected ? 'signup-preview__nav-item--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-current={selected ? 'page' : undefined}
      tabIndex={-1}
      onClick={() => onSelect(label)}
    >
      <span className="signup-preview__nav-icon-wrap sidebar-nav-icon">
        <Icon className="signup-preview__nav-icon" />
      </span>
      <span className="signup-preview__nav-label">{displayLabel || label}</span>
    </button>
  );
}

export default function SignupLivePreview({ orgName, username, workEmail, focusedField }) {
  const [activePage, setActivePage] = useState('Home');
  const workspaceName = derivePreviewOrgName(orgName);
  const displayName = deriveDisplayName(username);
  const workspaceInitial = deriveOrgInitial(workspaceName);
  const workspaceAvatarColor = stringAvatar(workspaceName).sx?.bgcolor || '#71717a';
  return (
    <aside className="signup-preview" aria-hidden="true">
      <div className="signup-preview__frame">
        <div className="signup-preview__sidebar">
          <div
            className={[
              'signup-preview__header',
              focusedField === 'orgName' ? 'signup-preview__header--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div
              className="signup-preview__org-logo"
              style={{ backgroundColor: workspaceAvatarColor }}
            >
              {workspaceInitial}
            </div>
            <span className="signup-preview__org-name">{workspaceName}</span>
          </div>

          <div className="signup-preview__scroll">
            <div className="signup-preview__section">
              {SIGNUP_PREVIEW_PRIMARY_ITEMS.map(label => (
                <PreviewNavItem
                  key={label}
                  label={label}
                  selected={activePage === label}
                  onSelect={setActivePage}
                />
              ))}
            </div>

            <div className="signup-preview__resource-group">
              <div className="signup-preview__section-header">Resources</div>
              <div className="signup-preview__section">
                {SIGNUP_PREVIEW_RESOURCE_ITEMS.map(label => (
                  <PreviewNavItem
                    key={label}
                    label={label}
                    selected={activePage === label}
                    onSelect={setActivePage}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="signup-preview__footer">
            <div className="signup-preview__credits">
              <span className="signup-preview__credits-icon">
                <BoltIcon />
              </span>
              <span className="signup-preview__credits-copy">
                <span className="signup-preview__credits-title">Credits</span>
                <span className="signup-preview__credits-subtitle">500 remaining</span>
              </span>
            </div>
          </div>
        </div>

        <div className="signup-preview__content">
          <div className="signup-preview__content-inner">
            <SignupPreviewPageDemo
              page={activePage}
              displayName={displayName}
              workEmail={workEmail}
            />
          </div>
        </div>
      </div>
    </aside>
  );
}
